import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { List, ListItem, ListItemText, ListItemButton, Backdrop, CircularProgress } from "@mui/material";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import Box from "@mui/material/Box"

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isNull } from "lodash";

import postnService from "@plannel/services/data/postn-service";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import cloneDeep from "lodash/cloneDeep";
import CodeUtil from "@plannel/pages/utils/code-util";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor:{ warning, dataEmpty, saveFail, saveSuccess, noSaveData } } = ColorUtil;
const PostnMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);

  const gridRef = useRef();

  const [selectLevelDialog, setSelectLevelDialog] = useState(false);
  const [currentPostn, setCurrentPostn] = useState({});
  const searchCode = useRef(reduxViewState?.searchCode ? reduxViewState.searchCode : "");
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [postnData, setPostnData] = useState([]);
  const [filteredPostn, setFilteredPostn] = useState([]);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoGroupColumnMemo = useMemo(() => {
    return {
      headerName: t("postnCd"),
      minWidth: 250,
      cellClass: 'stringType',
      cellRendererParams: {
        suppressCount: true,
      },
      valueSetter: (params) => {
        params.data.postnPath.pop();
        params.data.postnPath.push(params.newValue);
        params.data.postnCd = params.newValue;
      },
      valueGetter: (params) => {
        return params.data ? params.data.postnCd : params.node.key;
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPostn, t]);

  const excelDefs = [
    { headerName: "postnCd", field: "postnCd", cellClass: 'stringType', filterType: "string" }
  ];

  const columnDefs = [
    { headerName: "postnPath", field: "postnPath", type: ["defaultValueParser"], hide: true, suppressColumnsToolPanel: true },
    { headerName: "postnConfig", field: "postnConfig", type: ["defaultValueParser"], hide: true, suppressColumnsToolPanel: true },
    { headerName: "parentId", field: "parentId", type: ["defaultValueParser"], hide: true, suppressColumnsToolPanel: true },
    { headerName: "parentName", field: "parentName", type: ["defaultValueParser"], hide: true, suppressColumnsToolPanel: true }
  ];

  const commonDefs = [
    { headerName: "postnName", field: "name", minWidth: 200, flex: 12, filterType: "string" },
    { headerName: "descTxt", field: "descTxt", filterType: "string" },
    { headerName: "finalApvlFlg", field: "finalApvlFlg", type: ["booleanColumn"], minWidth: 80, flex: 8, filterType: "boolean", editable: (params) => params.data.activeFlg },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], minWidth: 80, flex: 8, editable: (params) => !params.data.finalApvlFlg, filterType: "boolean" },
    { headerName: "leafFlg", field: "leafFlg", type: ["booleanColumn"], minWidth: 80, flex: 8, filterType: "boolean" },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 11, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 11, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const checkActiveFlgChanges = () => {
        const allStateData = DataState.getAllStateData(gridRef.current.api);
        const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};
        
        return allStateData.some((row) => {
          const originalData = updatedOrigin[row.rowIndex];
          if (originalData) {
            return originalData.activeFlg !== row.activeFlg && row.activeFlg === false;
          }
          return false;
        });
      };
    
    const handleSaveClick = () => {
      gridRef.current?.api?.stopEditing();
        
      const hasActiveFlgChanges = checkActiveFlgChanges();
        
      if (hasActiveFlgChanges) {
        setDialogInfo({ open: true, title: "save", content: `${t("MSG_ActiveStatusChangedAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.postn, 'deactivate')}` }); 
      } else {  
        setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") });
      }
    };

  const getRequestParams = () => {
    const params = {};

    if (searchCode.current) {
      params.searchFilters = { postnCd: searchCode.current };
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    return params;
  }

  const refreshGridByFilter = (dataResults) => {
    setFilteredPostn(dataResults);
    DataState.initialize(gridRef.current?.api);
  }

  const retrievePostn = () => {
    const params = getRequestParams();

    postnService.getAll(params)
      .then(response => {
        const { results } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: dataEmpty, content: t("MSG_NoData") });
        }
        setPostnData(cloneDeep(results));
        refreshGridByFilter(results);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, {searchCode: searchCode.current, advancedFilters});
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  }

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs: [...columnDefs, ...commonDefs], viewName, initState: true });
  }

  const checkSameLevels = (arr) => {
    // 배열이 비어있거나, 모든 하위 배열의 길이가 같으면 true 반환
    if (arr?.length === 0) return true;
  
    // 첫 번째 하위 배열의 길이를 기준으로 비교
    const subArrayLength = arr?.[0].length;
  
    // 모든 하위 배열의 길이를 비교
    return arr.every(subArr => subArr?.length === subArrayLength);
  }

  const checkUpsertData = (allStateData) => {
    if (allStateData?.length > 0) {
      const filteredUpdateDatas = filteredPostn.filter(data => data.finalApvlFlg);
      const finalApvlPath = filteredUpdateDatas.map(data => data?.postnPath);
      if (!checkSameLevels(finalApvlPath)) {
        setSnackInfo({ open: true, severity: warning, content: t("MSG_PostnFinalAprroval") });
        return false;
      } 
      return true;
    } else {
      setSnackInfo({ open: true, severity: noSaveData, content: t("MSG_NoSaveData") });
      return false;
    }
  }

  const savePostn = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    for (const stateData of allStateData) {
      if (!stateData.postnCd || !stateData.name) {
        setSnackInfo({ open: true, severity: saveFail, content: t("MSG_MissingInput") });
        return false;
      }
    }

    if (checkUpsertData(allStateData)) {
      for (const row of allStateData) {
        if (row.rootFlg) {
          for (const data of filteredPostn) {
            if (data.parentPostn === null) {
              allStateData.push({...data});
            }
          }
        }

        for (const data of postnData) {
          if (row.id === data.id) {
            if (!row.activeFlg && data.activeFlg) {
              row.checkApproveFlg = false;
            }
            if (row.activeFlg && !data.activeFlg) {
              row.checkApproveFlg = true;
            }
          }
        }
      }
      
      setProgressOn(true);
      postnService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrievePostn();
        })
        .finally(() => {
          setProgressOn(false);
        });
    }
    return true;
  }

  const chkRows = () => {
    gridRef.current.api.stopEditing(false);

    const chkEmptyRows = [];
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        if (!data.id) {
          chkEmptyRows.push(data);
        }
      }
    );

    gridRef.current.api.applyTransaction({ remove: chkEmptyRows });
    setDialogInfo({ open: true, title: "delete", content: `${t("MSG_DeleteDataAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.postn, 'delete')}` })
  }

  const removePostn = () => {
    const postnIds = [];
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        postnIds.push(data.id)
      }
    );

    setProgressOn(true);
    postnService.remove(postnIds)
      .then(() => {
        setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_DeleteSuccess") });
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: saveFail, content: t("MSG_DeleteFailed") });
        debug(e);
      })
      .then(() => {
        retrievePostn();
      })
      .finally(() => {
        setProgressOn(false)
      });
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      savePostn()
    } else if (action === "DELETE") {
      removePostn()
    }
  }

  const addLv1Row = () => {
    const newRow = {
      postnPath: [`code${new Date().getMilliseconds()}`],
      id: null,
      name: null,
      descTxt: null,
      finalApvlFlg: false,
      leafFlg: false,
      activeFlg: true,
      rootFlg: false,
      verNum: 0
    };

    const addNewRow = gridRef.current.api.applyTransaction({ add: [newRow], addIndex: 0 }).add[0];
    DataState.setDataState(addNewRow);
  }

  const selectLevelDialogOpen = () => {
    const selectedRow = gridRef.current.api.getSelectedRows()[0];
    if (selectedRow?.id) {
      setCurrentPostn(selectedRow);
      setSelectLevelDialog(true);
    } else {
      addLv1Row();
    }
  }

  const selectLevelDialogClose = () => {
    setSelectLevelDialog(false);
  }

  const setRowLevelData = () => {
    const rowData = gridRef.current.api.getSelectedRows()[0] ? gridRef.current.api.getSelectedRows()[0] : { postnLv: 1 };
    setCurrentPostn(rowData);
  }

  const selectLevel = (data) => {
    if (isNull(data.parentPostn)) {
      addLv1Row();
      setSelectLevelDialog(false);
    } else {
      const path = data.postnPath.slice();
      path.pop();
      path.push(`code${new Date().getMilliseconds()}`);

      const newRow = {
        postnPath: path,
        parentPostn: data.parentPostn,
        name: null,
        descTxt: null,
        finalApvlFlg: false,
        leafFlg: false,
        activeFlg: true,
        rootFlg: false,
        verNum: 0
      };

      const addNewRow = gridRef.current.api.applyTransaction({ add: [newRow], addIndex: 0 }).add[0];
      DataState.setDataState(addNewRow);
      setSelectLevelDialog(false);
    }
  }

  const selectHighLevel = () => {
    const newRow = {
      postnPath: [`code${new Date().getMilliseconds()}`],
      id: null,
      name: null,
      descTxt: null,
      finalApvlFlg: false,
      leafFlg: false,
      activeFlg: true,
      rootFlg: true,
      verNum: 0
    };
    const addNewRow = gridRef.current.api.applyTransaction({ add: [newRow], addIndex: 0 }).add[0];
    DataState.setDataState(addNewRow);
    setSelectLevelDialog(false);
  }

  const selectLowLevel = (data) => {
    const path = data.postnPath.slice();
    path.push(`code${new Date().getMilliseconds()}`);

    const newRow = {
      postnPath: path,
      parentPostn: data,
      name: null,
      descTxt: null,
      finalApvlFlg: false,
      leafFlg: false,
      activeFlg: true,
      rootFlg: false,
      verNum: 0
    };

    const addNewRow = gridRef.current.api.applyTransaction({ add: [newRow], addIndex: 0 }).add[0];
    DataState.setDataState(addNewRow);
    setSelectLevelDialog(false);
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    DataState.initialize(gridRef.current?.api);
    retrievePostn();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <TextFieldSearch sx={{ mr: 1.5, width: 250 }} label={"postnCd"} searchCode={searchCode} retrieve={retrievePostn} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={postnService} params={{ postnCd: searchCode.current }} columnDefs={[...excelDefs, ...commonDefs]} />
          <AddButton onClick={selectLevelDialogOpen} />
          <RemoveButton onClick={chkRows} />
          <SaveButton onClick={handleSaveClick} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" sx={{ pb: 1 }} >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredPostn}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              autoGroupColumnDef={autoGroupColumnMemo}
              treeData
              groupDefaultExpanded={-1}
              getDataPath={(data) => data.postnPath}
              onCellClicked={setRowLevelData}
              onGridReady={onGridReady}
              enterNavigatesVertically
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={[...excelDefs, ...commonDefs]}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <Backdrop sx={{ color: '#fff', zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <BaseConfigModal open={selectLevelDialog} onClose={selectLevelDialogClose} title={"Select Parent Position"}>
        <List sx={{ pt: 0 }}>
          {currentPostn.parentPostn === null ?
            <ListItem key={`${currentPostn.level - 1}`}>
              <ListItemButton onClick={() => selectHighLevel()} >
                <ListItemText primary={`Parent: ${currentPostn.parentPostn === null ? t("rootNodes") : currentPostn.parentPostn?.name}`} sx={{ textAlign: "center" }} />
              </ListItemButton>
            </ListItem>
            : <Box></Box>}
          <ListItem key={currentPostn.parentPostn?.postnCd}>
            <ListItemButton onClick={() => selectLevel(currentPostn)} >
              <ListItemText primary={`Parent: ${currentPostn.parentPostn === null ? t("none") : currentPostn.parentPostn?.name}`} sx={{ textAlign: "center" }} />
            </ListItemButton>
          </ListItem>
          <ListItem key={`${currentPostn.postnCd}`}>
            <ListItemButton onClick={() => selectLowLevel(currentPostn)} >
              <ListItemText primary={`Parent: ${currentPostn.name}`} sx={{ textAlign: "center" }} />
            </ListItemButton>

          </ListItem>
        </List>
      </BaseConfigModal>
    </>
  );
};

export default withTranslation()(PostnMaster);
